/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

import { TemplateFieldEditor } from '@/components/templates-v2/document-generation/templates/TemplateInstantiator';
import { Select } from '@/components/ui/catalyst/select';
import type { ReadTemplateSummary } from '@/services/api/generated/templatesV2Service';
import type { MatterFlattenedContext } from '@/services/api/matterFlattenedContextService';

interface TemplateSectionProps {
  isFoia: boolean;
  templates: ReadTemplateSummary[];
  templatesLoading: boolean;
  refetchTemplates: () => void;
  selectedTemplateId: string;
  onTemplateChange: (id: string) => void;
  matterContext?: MatterFlattenedContext;
  overrides: Record<string, string>;
  onOverrideChange: (fieldId: string, value: string) => void;
  validation: {
    isValid: boolean;
    missingFields: Array<{ id: string; displayName: string }>;
    missingCount: number;
  };
  firmId?: string;
}

export const TemplateSection = ({
  isFoia,
  templates,
  templatesLoading,
  refetchTemplates,
  selectedTemplateId,
  onTemplateChange,
  matterContext,
  overrides,
  onOverrideChange,
  validation,
  firmId,
}: TemplateSectionProps) => {
  const [showFields, setShowFields] = useState(false);

  if (!isFoia) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="text-xs text-blue-900">
          <span className="font-medium">Template:</span> Generic Finch police
          report request letter
        </div>
      </div>
    );
  }

  if (templates.length === 0 && !templatesLoading) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-xs font-medium text-amber-900">
              No FOIA Template
            </div>
            <div className="text-xs text-amber-700 mt-0.5">
              Your firm doesn&apos;t have a FOIA letter template configured.
            </div>
            <div className="mt-2 flex items-center gap-2">
              {firmId && (
                <button
                  type="button"
                  onClick={() => {
                    window.open(`/firms/${firmId}/templates`, '_blank');
                  }}
                  className="text-xs font-medium text-amber-900 hover:text-amber-800 underline"
                >
                  Go to Templates →
                </button>
              )}
              <button
                type="button"
                onClick={() => refetchTemplates()}
                className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 hover:text-amber-800 px-2 py-1 rounded hover:bg-amber-100"
              >
                <ArrowPathIcon className="h-3 w-3" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Template selector */}
      {templates.length > 0 && (
        <div>
          <label
            htmlFor="foia-template-select"
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            FOIA Template:
          </label>
          <Select
            id="foia-template-select"
            value={selectedTemplateId}
            onChange={(e) => onTemplateChange(e.target.value)}
            disabled={templatesLoading}
          >
            {templatesLoading ? (
              <option value="">Loading...</option>
            ) : (
              templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.display_name || t.name}
                  {t.is_default && ' (Default)'}
                </option>
              ))
            )}
          </Select>
        </div>
      )}

      {/* Field validation */}
      {selectedTemplateId && matterContext && (
        <div className="border rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowFields(!showFields)}
            className={`w-full flex items-center justify-between p-3 transition-colors ${
              validation.missingCount > 0
                ? 'bg-amber-50 hover:bg-amber-100'
                : 'bg-green-50 hover:bg-green-100'
            }`}
          >
            <div className="flex items-center gap-2">
              {showFields ? (
                <ChevronUpIcon className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 text-gray-600" />
              )}
              <span className="text-xs font-medium text-gray-900">
                Template Fields
              </span>
            </div>
            {validation.missingCount > 0 ? (
              <div className="flex items-center gap-1.5">
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-600" />
                <span className="text-xs font-medium text-amber-900">
                  {validation.missingCount} missing
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <CheckCircleIcon className="h-4 w-4 text-green-600" />
                <span className="text-xs font-medium text-green-900">
                  Ready
                </span>
              </div>
            )}
          </button>
          {showFields && (
            <div className="p-3 border-t bg-white">
              <TemplateFieldEditor
                templateId={selectedTemplateId}
                matterContext={
                  matterContext as Record<string, string | undefined>
                }
                overrides={overrides}
                setOverride={onOverrideChange}
                inline
              />
            </div>
          )}
          {!showFields && validation.missingCount > 0 && (
            <div className="px-3 pb-3 bg-amber-50">
              <div className="text-xs text-amber-800">
                Missing:{' '}
                {validation.missingFields
                  .slice(0, 3)
                  .map((f) => f.displayName)
                  .join(', ')}
                {validation.missingCount > 3 &&
                  ` +${validation.missingCount - 3} more`}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
